import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const ALLOWED_PATH_PREFIXES = [
  "/recepcionista",
  "/total-usuarios",
  "/total-pagos",
  "/gestion-noticias",
  "/gestion-reservas",
  "/arqueo-caja",
  "/pagos",
];

export const isRecepcionistaAllowedPath = (pathname: string) =>
  ALLOWED_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Hook that checks if the current user has the 'recepcionista' role.
 *
 * - `isRecepcionista`: true if the user has the recepcionista role granted in
 *   `user_roles` (via `has_role`). This is what gates RLS-protected actions
 *   and access to the /recepcionista panel.
 * - `isPureRecepcionista`: true only when the user's primary `profiles.role`
 *   is `recepcionista`. Pure recepcionistas are auto-redirected to the panel
 *   and cannot navigate the rest of the app. Users with a dual role (e.g. a
 *   profesor that ALSO has recepcionista access) are NOT considered "pure"
 *   and keep free navigation across the app.
 */
export const useRecepcionistaRedirect = () => {
  const { user } = useAuth();
  const [isRecepcionista, setIsRecepcionista] = useState<boolean | null>(null);
  const [isPureRecepcionista, setIsPureRecepcionista] = useState<boolean | null>(null);
  const [isCheckingRole, setIsCheckingRole] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsRecepcionista(null);
      setIsPureRecepcionista(null);
      setIsCheckingRole(false);
      return;
    }

    const check = async () => {
      try {
        const [{ data: hasRecepRole, error: roleError }, { data: profile, error: profileError }] =
          await Promise.all([
            supabase.rpc("has_role", {
              _user_id: user.id,
              _role: "recepcionista",
            }),
            supabase.from("profiles").select("role").eq("id", user.id).maybeSingle(),
          ]);

        if (roleError) {
          console.error("useRecepcionistaRedirect role error:", roleError);
        }
        if (profileError) {
          console.error("useRecepcionistaRedirect profile error:", profileError);
        }

        const hasRole = hasRecepRole === true;
        const isPure = hasRole && profile?.role === "recepcionista";

        setIsRecepcionista(hasRole);
        setIsPureRecepcionista(isPure);
      } catch (e) {
        console.error("useRecepcionistaRedirect unexpected:", e);
        setIsRecepcionista(false);
        setIsPureRecepcionista(false);
      } finally {
        setIsCheckingRole(false);
      }
    };

    check();
  }, [user]);

  return { isRecepcionista, isPureRecepcionista, isCheckingRole };
};

export const checkIsRecepcionista = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "recepcionista",
    });
    if (error) return false;
    return data === true;
  } catch {
    return false;
  }
};
